import { LayaGL } from "../../layagl/LayaGL";
import { DrawType } from "../../RenderEngine/RenderEnum/DrawType";
import { IndexFormat } from "../../RenderEngine/RenderEnum/IndexFormat";
import { MeshTopology } from "../../RenderEngine/RenderEnum/RenderPologyMode";
import { Browser } from "../../utils/Browser";
import { Pool } from "../../utils/Pool";
import { FastSinglelist } from "../../utils/SingletonList";
import { BlendModeHandler } from "../canvas/BlendMode";
import { Shader2D } from "../shader/d2/Shader2D";
import { GraphicsShaderInfo } from "../shader/d2/value/GraphicsShaderInfo";
import { ShaderDefines2D } from "../shader/d2/ShaderDefines2D";
import { SubmitKey } from "./SubmitKey";
export class SubmitBase {
    get mesh() {
        return this._mesh;
    }
    set mesh(value) {
        if (this._mesh == value)
            return;
        if (this.indexView && this._mesh) {
            this._mesh.clearIndexView(this.indexView);
            this.indexView = null;
        }
        this._mesh = value;
        if (value) {
            this.renderElement.geometry.bufferState = value.bufferState;
            this._bufferBlock.vertexBuffer = value._buffer.vertexBuffer;
            this._bufferBlockDirty = true;
        }
    }
    get material() {
        return this._material;
    }
    set material(value) {
        if (this._material == value)
            return;
        if (this._material) {
            this._material._removeOwnerElement(this.renderElement);
        }
        this._material = value;
        if (value) {
            this.renderElement.subShader = value.shader.getSubShaderAt(0);
            this.renderElement.materialShaderData = value.shaderData;
            value._setOwner2DElement(this.renderElement);
        }
        else {
            this.renderElement.subShader = Shader2D.graphicsShader.getSubShaderAt(0);
            this.renderElement.materialShaderData = null;
        }
    }
    constructor() {
        this.clipInfoID = -1;
        this._id = 0;
        this._renderType = 0;
        this._key = new SubmitKey();
        this.indexCount = 0;
        this.indices = [];
        this._internalInfo = null;
        this.renderStateIsBySprite = true;
        this.vertexs = new FastSinglelist;
        this.renderElement = null;
        this._bufferBlock = null;
        this._currentVertexBlocks = new FastSinglelist;
        this._lastVertexBlocks = new FastSinglelist;
        this._bufferBlockDirty = true;
        this._cacheInfo = null;
        this._id = ++SubmitBase.ID;
    }
    _getCacheInfo() {
        if (!this._cacheInfo) {
            this._cacheInfo = {
                chunks: [],
                vertexCount: 0,
            };
        }
        return this._cacheInfo;
    }
    clear() {
        this._key.clear();
        this._internalInfo.clear();
        this.indexCount = 0;
        this.vertexs.length = 0;
        this._currentVertexBlocks.length = 0;
        this._cacheInfo = null;
    }
    appendData(info) {
        let originLength = this._currentVertexBlocks.length;
        for (let i = 0, n = info.vertexBlocks.length; i < n; i++) {
            this._currentVertexBlocks.add(info.vertexBlocks[i]);
            if (info.vertexBlocks[i] !== this._lastVertexBlocks.elements[i + originLength]) {
                this._bufferBlockDirty = true;
            }
        }
    }
    getVertexBlock() {
        let vertexBlock;
        if (this.vertexs.elements.length > this.vertexs.length) {
            vertexBlock = this.vertexs.elements[this.vertexs.length];
            this.vertexs.length++;
        }
        else {
            vertexBlock = LayaGL.render2DRenderPassFactory.createGraphic2DVertexBlock();
            this.vertexs.add(vertexBlock);
        }
        return vertexBlock;
    }
    prepare() {
        let element = this.renderElement;
        this.vertexs.clean();
        let indexView = this.indexView;
        let indexViewChanged = false;
        if (!indexView || indexView.length !== this.indexCount) {
            if (indexView) {
                this.mesh.clearIndexView(indexView);
            }
            indexView = this.mesh.checkIndex(this.indexCount);
            this.indexView = indexView;
            this._bufferBlock.indexView = indexView;
            indexView.setGeometry(element.geometry);
            indexViewChanged = true;
        }
        let vertexBlocksChanged = this._currentVertexBlocks.length !== this._lastVertexBlocks.length || this._bufferBlockDirty;
        let needUpdateIndexData = indexViewChanged || vertexBlocksChanged;
        if (this.indices.length !== this.indexCount) {
            this.indices.length = this.indexCount;
        }
        indexView.setData(this.indices);
        if (vertexBlocksChanged && Browser.onLayaRuntime) {
            this._bufferBlock.vertexs = this.vertexs.elements;
        }
        this._bufferBlockDirty = needUpdateIndexData;
        let useCustomMaterial = this.material ? 1 : 0;
        let texture;
        let textureHost = this._internalInfo.textureHost;
        if (textureHost)
            texture = textureHost.bitmap || textureHost;
        let defineBits = ShaderDefines2D.getPerElementDefineBits(this._internalInfo.shaderData);
        element.typeKey = this._key.blendShader
            | (useCustomMaterial ? ShaderDefines2D.TYPEKEY_CUSTOM_MATERIAL : 0)
            | defineBits;
        element.textureKey = texture ? texture.id : 0;
        if (this._cacheInfo) {
            this._cacheInfo.texture = texture;
            this._cacheInfo.blendShader = this._key.blendShader;
        }
        this._lastVertexBlocks.length = 0;
        for (let i = 0, n = this._currentVertexBlocks.length; i < n; i++) {
            this._lastVertexBlocks.add(this._currentVertexBlocks.elements[i]);
        }
        this._currentVertexBlocks.length = 0;
        return this._bufferBlock;
    }
    destroy() {
        this.clear();
        this.material = null;
        this.mesh = null;
        this._internalInfo.destroy();
        this._internalInfo = null;
        this._bufferBlock = null;
        this.vertexs.destroy();
        this.vertexs = null;
        this._lastVertexBlocks.destroy();
        this._lastVertexBlocks = null;
        this._currentVertexBlocks.destroy();
        this._currentVertexBlocks = null;
        if (this.renderElement) {
            SubmitBase._pool.recover(this.renderElement);
        }
        this.renderElement = null;
    }
    update(runner) {
        let sBlendMode = runner.sprite._struct.blendMode;
        var blendType = runner._nBlendType;
        this._key.blendShader = blendType;
        if (runner.globalCompositeOperation != sBlendMode) {
            BlendModeHandler.setShaderData(blendType, this._internalInfo.shaderData);
            this._internalInfo._blend = blendType;
            this.renderStateIsBySprite = false;
        }
    }
    static create(runner) {
        var o = new SubmitBase();
        o._internalInfo = new GraphicsShaderInfo();
        o.renderElement = SubmitBase._pool.take();
        o.renderElement.primitiveShaderData = o._internalInfo.shaderData;
        o._bufferBlock = LayaGL.render2DRenderPassFactory.createGraphic2DBufferBlock();
        o._bufferBlock.vertexs = o.vertexs.elements;
        o.renderElement.subShader = Shader2D.graphicsShader.getSubShaderAt(0);
        o.renderElement.materialShaderData = null;
        o.update(runner);
        return o;
    }
}
SubmitBase._pool = Pool.createPool2(() => {
    let element = LayaGL.render2DRenderPassFactory.createPrimitiveRenderElement2D();
    element.renderStateIsBySprite = false;
    element.nodeCommonMap = ["Sprite2D"];
    return element;
}, (element, needGeometry) => {
    if (needGeometry || needGeometry == null) {
        element.geometry = LayaGL.renderDeviceFactory.createRenderGeometryElement(MeshTopology.Triangles, DrawType.DrawElement);
        element.geometry.indexFormat = IndexFormat.UInt16;
    }
    else {
        if (element.geometry) {
            element.geometry.destroy();
            element.geometry = null;
        }
    }
}, (element) => {
    if (element.geometry) {
        element.geometry.clearRenderParams();
        element.geometry.bufferState = null;
    }
    element.materialShaderData = null;
    element.value2DShaderData = null;
    element.primitiveShaderData = null;
    element.globalShaderData = null;
    element.owner = null;
    element.subShader = null;
    element.renderStateIsBySprite = false;
    element.typeKey = 0;
    element.textureKey = 0;
});
SubmitBase.ID = 1;
SubmitBase.RENDERBASE = new SubmitBase();
